<template>
  <v-menu
    v-if="shouldRenderTooltip"
    v-model="isActive"
    :activator="targetElement"
    transition="fade-transition"
    location="top end"
    :close-on-content-click="closeOnContentClick"
    :open-on-hover="false"
    :open-on-click="false"
    :min-width="100"
    @mouseenter="handleTooltipMouseEnter"
    @mouseleave="handleTooltipMouseLeave"
    @click="closeOnContentClick ? handleTooltipMouseLeave($event) : null"
  >
    <slot :tooltip-props="localTooltipProps" />
  </v-menu>
</template>

<script setup>
import { ref, watch, onBeforeUnmount, nextTick } from 'vue';

/**
 * Props
 */
const props = defineProps({
  /**
   * The target HTML element to attach the tooltip to.
   * When null, the tooltip will not be displayed.
   */
  target: {
    type: [HTMLElement, null],
    default: null,
  },
  tooltipProps: {
    type: Object,
    default: () => ({}),
  },
  openDelay: {
    type: Number,
    default: 500,
  },
  closeDelay: {
    type: Number,
    default: 200,
  },
  closeOnContentClick: {
    type: Boolean,
    default: false,
  },
});

/**
 * State
 */
const isActive = ref(false);
const targetElement = ref(null);
const shouldRenderTooltip = ref(false);
const isMouseOverTooltip = ref(false);
const localTooltipProps = ref({});
let hideTimeout = null;
let showTimeout = null;

/**
 * Clear any pending timeouts
 */
const clearHideTimeout = () => {
  if (hideTimeout) {
    clearTimeout(hideTimeout);
    hideTimeout = null;
  }
};

const clearShowTimeout = () => {
  if (showTimeout) {
    clearTimeout(showTimeout);
    showTimeout = null;
  }
};

const fixSeverity = (severity) => ({
  'red-solid': 'performance',
  'blue-solid': 'brand',
  'yellow-solid': 'compliance',
  'green-solid': 'kudos',
}[severity] || severity);

/**
 * Handle mouse enter on tooltip content
 */
const handleTooltipMouseEnter = () => {
  isMouseOverTooltip.value = true;
  clearHideTimeout();
  isActive.value = true;
};

/**
 * Handle mouse leave on tooltip content
 */
const handleTooltipMouseLeave = () => {
  isMouseOverTooltip.value = false;
  
  // If target is null, hide after a short delay
  if (!props.target) {
    hideTimeout = setTimeout(() => {
      isActive.value = false;
      // Clean up after hiding
      setTimeout(() => {
        if (!isMouseOverTooltip.value && !props.target) {
          targetElement.value = null;
          shouldRenderTooltip.value = false;
        }
      }, props.closeDelay);
    }, props.closeDelay);
  }
};

const show = (newTarget) => {
  // Target set - prepare to show tooltip after delay
  clearHideTimeout();
  clearShowTimeout();

  // Update local copy with new tooltip props (never clear, only update)
  localTooltipProps.value = {
    tooltips: props.tooltipProps.tooltips?.map((tooltip) => ({
      ...tooltip,
      severity: fixSeverity(tooltip.severity),
      category: fixSeverity(tooltip.category),
    })) || [],
  };

  nextTick(() => {
    // Set up position for the tooltip
    targetElement.value = newTarget;
  });

  // Show after openDelay timeout if target is still there
  showTimeout = setTimeout(() => {
    shouldRenderTooltip.value = true;

    if (props.target) {
      isActive.value = true;
    } else {
      // Target was removed during delay, clean up
      targetElement.value = null;
      shouldRenderTooltip.value = false;
    }
    showTimeout = null;
  }, props.openDelay);
};

const hide = () => {
  // Target removed - cancel show if pending
  clearShowTimeout();
  
  // Start grace period for hiding
  // Keep targetElement and shouldRenderTooltip as-is to maintain position
  // Keep localTooltipProps to keep the content intact
  hideTimeout = setTimeout(() => {
    if (isMouseOverTooltip.value) {
      return;
    }

    isActive.value = false;

    // Clean up after hiding
    if (!isMouseOverTooltip.value && !props.target) {
      targetElement.value = null;
      shouldRenderTooltip.value = false;
      localTooltipProps.value = { tooltips: [] };
    }
  }, props.closeDelay);
};

/**
 * Watch for target prop changes
 */
watch(
  () => props.target,
  (newTarget) => newTarget ? show(newTarget) : hide(),
  { immediate: true },
);

/**
 * Cleanup on unmount
 */
onBeforeUnmount(() => {
  clearHideTimeout();
  clearShowTimeout();
});
</script>
